import { useCallback } from 'react';

import type { UtilityContext } from '@change-corgi/core/react/utilityContext';

import { createEndorsement } from '../../api/createEndorsement';
import type { EndorsementFormData } from '../types';

import type { EndorsementError } from './useEndorsementForm';

type SubmitParams = {
	petitionId: string;
	formData: EndorsementFormData;
	utilityContext: UtilityContext;
	track: (event: string, data?: Record<string, unknown>) => Promise<boolean>;
	onSuccess: () => void;
	onError: (error: EndorsementError) => void;
	setIsSubmitting: (value: boolean) => void;
	trackSubmitClick?: boolean;
};

function getErrorType(msg: string): EndorsementError['type'] {
	if (msg.includes('authenticated') || msg.includes('401')) return 'UNAUTHORIZED';
	if (msg.includes('400')) return 'VALIDATION';
	return 'SERVER_ERROR';
}

function buildEndorsementInput(petitionId: string, formData: EndorsementFormData) {
	return {
		petitionId,
		name: formData.name,
		title: formData.title || undefined,
		url: formData.url || undefined,
		content: formData.statement || undefined,
		...(formData.photoUrl && { photo: { url: formData.photoUrl } }),
	};
}

export function useEndorsementSubmit(params: SubmitParams) {
	const {
		petitionId,
		formData,
		utilityContext,
		track,
		onSuccess,
		onError,
		setIsSubmitting,
		trackSubmitClick = false,
	} = params;

	const submit = useCallback(async () => {
		setIsSubmitting(true);
		if (trackSubmitClick) {
			void track('starter_dashboard_endorsement_submit_click');
		}
		try {
			const input = buildEndorsementInput(petitionId, formData);
			const response = await createEndorsement(input, utilityContext);
			if (response.success) {
				void track('starter_dashboard_endorsement_create_success');
				onSuccess();
			} else {
				const msg = response.errorMessage || 'Failed to create endorsement';
				void track('starter_dashboard_endorsement_create_error', { error: msg });
				onError({ message: msg, type: getErrorType(msg) });
			}
		} catch (e) {
			const msg = e instanceof Error ? e.message : 'Unexpected error';
			void track('starter_dashboard_endorsement_create_error', { error: msg });
			onError({ message: msg, type: 'NETWORK_ERROR' });
		} finally {
			setIsSubmitting(false);
		}
	}, [petitionId, formData, utilityContext, track, onSuccess, onError, setIsSubmitting, trackSubmitClick]);

	return { submit };
}
